/**
 * Risk tone for a loan's LTV — "danger" | "caution" | "safe" | "unknown" | "none".
 *
 * The backend already classifies LTV against the basket's warning and breach
 * thresholds (`basketLtv.js` → `loan.ltvStatus`), and the LTV monitor renders that
 * classification. The same warning and breach thresholds drive every LTV view,
 * so the loan page cannot disagree with the monitor about the same loan.
 *
 * `unknown` exists because green is a claim, not a default: with no thresholds and
 * no threshold there is nothing to judge the ratio against, and saying "safe" then
 * is an assertion the data does not support.
 */
export function ltvTone(loan) {
  const { currentLTV, ltvStatus } = loan || {};
  if (currentLTV == null) return "none";

  const basket = loan.basket || {};
  if (ltvStatus === "BREACH") return "danger";
  if (ltvStatus === "WARNING") return "caution";

  const judged = Boolean(basket.ltvBreachThreshold || basket.ltvWarningThreshold);
  return judged ? "safe" : "unknown";
}

/**
 * The alert a loan's LTV warrants, or null when it warrants none.
 *
 * Shares its precedence with `ltvTone` on purpose: its threshold is always the
 * backend's warning or breach threshold.
 *
 * `unknown` raises nothing: with no limit configured there is no line to have
 * crossed, and a false alarm is its own kind of wrong.
 *
 * @returns {{severity: "error"|"warning", limit: number, limitLabel: string}|null}
 */
export function ltvAlert(loan) {
  const tone = ltvTone(loan);
  if (tone !== "danger" && tone !== "caution") return null;

  const basket = loan.basket || {};
  if (tone === "danger") {
    return { severity: "error", limit: Number(basket.ltvBreachThreshold), limitLabel: "סף הפרה" };
  }

  return { severity: "warning", limit: Number(basket.ltvWarningThreshold), limitLabel: "סף אזהרה" };
}

export const LTV_TONE_CLASS = {
  danger: "kpi-ltv-danger",
  caution: "kpi-ltv-caution",
  safe: "kpi-ltv-safe",
  unknown: "kpi-ltv-unknown",
  none: "",
};
